import React, { useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';

// Only enable Cloudflare Image Resizing when explicitly opted in via VITE_CF_IMAGES=true.
// Requires "Resize Images" enabled in Cloudflare Pages project settings.
const CF = typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_CF_IMAGES === 'true';

function cfUrl(src: string, width: number, quality: number): string {
  if (!CF || !src || src.startsWith('data:') || src.includes('cdn-cgi')) return src;
  return `/cdn-cgi/image/width=${width},format=webp,quality=${quality}/${src}`;
}

function buildSrcSet(src: string, widths: number[], quality: number): string {
  return widths.map(w => `${cfUrl(src, w, quality)} ${w}w`).join(', ');
}

interface Props {
  src: string | null | undefined;
  alt: string;
  className?: string;
  sizes?: string;
  displayWidth?: number;
  widths?: number[];
  quality?: number;
  onError?: () => void;
}

export const OptimizedImage: React.FC<Props> = ({
  src,
  alt,
  className = '',
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  displayWidth = 800,
  widths = [400, 800, 1200],
  quality = 85,
  onError,
}) => {
  const [loaded, setLoaded]   = useState(false);
  const [errored, setErrored] = useState(false);

  // Si no hay src o falló la carga de la imagen, mostrar un placeholder genérico estético
  if (!src || errored) {
    return (
      <div className={`flex flex-col items-center justify-center bg-slate-100 text-slate-400 gap-2 w-full h-full select-none ${className}`}>
        <ImageIcon className="w-8 h-8 stroke-[1.25] text-slate-400/80 animate-pulse" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400/80">Sin imagen</span>
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 bg-slate-100 animate-pulse" aria-hidden />
      )}
      <img
        src={cfUrl(src, displayWidth, quality)}
        srcSet={buildSrcSet(src, widths, quality)}
        sizes={sizes}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => { setErrored(true); setLoaded(true); onError?.(); }}
        className={`transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
      />
    </>
  );
};
